/**
 * useCoverage.ts — fetch the app's loaded-region coverage once on mount (T-092).
 *
 * Backs the de-hardcode of the 4-state ceiling: MapShell reads this to derive the
 * default map camera (A1, from `extent`) and the coverage banner (A2, from `states`),
 * so loading a new region needs a data run, not a frontend edit.
 *
 * On error it resolves to empty coverage (states: [], extent: null) rather than throwing,
 * so the map still renders at the national default view instead of a broken page.
 */
import { useEffect, useState } from 'react'
import { fetchCoverage } from '../lib/api'
import type { Coverage, CoverageState } from '../lib/api'

export interface UseCoverageResult {
  states: CoverageState[]
  extent: [number, number, number, number] | null
  loading: boolean
  error: Error | null
}

export function useCoverage(): UseCoverageResult {
  const [state, setState] = useState<UseCoverageResult>({
    states: [],
    extent: null,
    loading: true,
    error: null,
  })

  useEffect(() => {
    let cancelled = false
    fetchCoverage()
      .then((c: Coverage) => {
        if (cancelled) return
        setState({ states: c.states, extent: c.extent, loading: false, error: null })
      })
      .catch((err: unknown) => {
        if (cancelled) return
        setState({
          states: [],
          extent: null,
          loading: false,
          error: err instanceof Error ? err : new Error(String(err)),
        })
      })
    return () => {
      cancelled = true
    }
  }, [])

  return state
}
